import { Model, Factory, belongsTo } from 'miragejs'
import {
  randFirstName,
  randLastName,
  randEmail,
  randJobTitle,
  randPhoneNumber,
  randNumber,
  randPastDate,
  randChanceBoolean,
  randAddress,
  randFutureDate,
} from '@ngneat/falso'
import { sortable, pageable, createErrorResponse } from './utils.js'
import { getDateTimeTimestamp } from '@/utils/date/getter.js'

import type { ModelDefinition } from 'miragejs/-types'
import type { SchemaAttributes } from '../types.js'
import type { MirageServer, MockServer } from './server.js'

export type LocumUsersData = SchemaAttributes<'LocumUser'>

export const model = Model.extend({
  earliestAssignment: belongsTo('locumAssignment'),
}) as ModelDefinition<LocumUsersData>

export const factory = Factory.extend<LocumUsersData>({
  first_name() {
    return randFirstName()
  },
  last_name() {
    return randLastName()
  },
  email() {
    return randEmail()
  },
  permission_group() {
    return randJobTitle()
  },
  initials() {
    return 'AB'
  },
  title() {
    return ''
  },
  phone() {
    return randPhoneNumber()
  },
  work_role() {
    return randChanceBoolean({ chanceTrue: 0.5 }) ? 'ADMINISTRATIVE' : 'VET'
  },
  is_locum() {
    return true
  },
  is_active() {
    return true
  },
  last_login() {
    return randPastDate().toISOString()
  },
  assignments_count() {
    return randNumber({ min: 0, max: 10 }).toString()
  },
})

export function routes(server: MockServer) {
  server.get('/locum_users', function (this: MirageServer, schema, request) {
    const locumUsers = schema.all('locumUser')

    // Apply sorting if requested
    const sorted = sortable(locumUsers, request)

    // Apply pagination if requested
    const { data, meta } = pageable(sorted, request)

    // Return serialized response with pagination meta
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })

  server.get(
    '/locum_users/:parent_lookup_user/locum_assignments',
    function (this: MirageServer, schema, request) {
      const locumUser = schema.find(
        'locumUser',
        request.params.parent_lookup_user,
      )
      const assignments = locumUser?.earliestAssignment
        ? [locumUser.earliestAssignment]
        : []
      return this.serialize({ data: assignments })
    },
  )

  server.post(
    '/locum_users/:parent_lookup_user/locum_assignments',
    function (this: MirageServer, schema, request) {
      const { data } = JSON.parse(request.requestBody)
      const userId = request.params.parent_lookup_user
      const locumUser = schema.find('locumUser', userId)
      const assignment = schema.create('locumAssignment', data.attributes)
      if (locumUser) {
        locumUser.update({ earliestAssignment: assignment })
      }
      return assignment
    },
  )

  server.get(
    '/locum_users/:id',
    function (this: MirageServer, schema, request) {
      const departments = schema
        .all('department')
        .models.map((department) => department.attrs)

      const departmentAddresses = new Map<
        string,
        { street: string; city: string }
      >()
      departments.forEach((department) => {
        const address = randAddress()
        departmentAddresses.set(department.id, {
          street: address.street,
          city: address.city,
        })
      })

      const firstDepartment = departments[0]
      const allOtherDepartments = departments.slice(1)

      const departmentTemplate = (id: string) => {
        const department = departments.find((dept) => dept.id === id)
        const address = departmentAddresses.get(id)

        const attributes: Record<string, string | undefined> = {
          name: department?.name,
          street_address: address?.street,
          street_address_2: address?.city,
        }

        // Only include date attributes for non-permanent assignments
        if (id !== firstDepartment.id) {
          attributes.current_locum_start_date = randPastDate().toISOString()
          attributes.current_locum_end_date = randFutureDate().toISOString()
        }

        return {
          type: 'Department',
          id,
          attributes,
        }
      }

      return {
        data: {
          type: 'LocumUser',
          id: request.params.id,
          relationships: {
            available_departments_permanent: {
              data: firstDepartment
                ? [{ type: 'Department', id: firstDepartment.id }]
                : [],
            },
            available_departments_non_permanent: {
              data: allOtherDepartments.map((department) => ({
                type: 'Department',
                id: department.id,
              })),
            },
          },
        },
        included: [
          ...(firstDepartment ? [departmentTemplate(firstDepartment.id)] : []),
          ...allOtherDepartments.map((department) =>
            departmentTemplate(department.id),
          ),
        ],
      }
    },
  )

  server.patch(
    '/locum_users/:parent_lookup_user/locum_assignments/:id',
    function (this: MirageServer, schema, request) {
      const assignment = schema.find('locumAssignment', request.params.id)
      if (!assignment) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Assignment not found' }],
        })
      }
      return assignment
    },
  )

  server.delete(
    '/locum_users/:parent_lookup_user/locum_assignments/:id',
    function (this: MirageServer, schema, request) {
      const assignment = schema.find('locumAssignment', request.params.id)
      if (!assignment) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Assignment not found' }],
        })
      }
      return assignment
    },
  )
}

export function seeds(server: MockServer) {
  // Create locum users
  const locumUsers = server.createList('locumUser', 30)

  // Create some locum assignments and link them to users
  locumUsers.forEach((locumUser) => {
    // Create 1-3 assignments per user
    const assignments = server.createList(
      'locumAssignment',
      randNumber({ min: 0, max: 3 }),
    )

    // Set the earliest assignment (first one chronologically)
    const earliestAssignment = assignments.sort(
      (a, b) =>
        getDateTimeTimestamp(a.start_date) - getDateTimeTimestamp(b.start_date),
    )[0]

    if (earliestAssignment) {
      locumUser.update({ earliestAssignment })
    }
  })
}
